import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, NavLink, useParams } from "react-router-dom";
import { Avatar, AvatarGradientFallback, Card, cn, Heading, initialsFor, Text } from "@athena/ui";
import { useClient } from "./AlumniProfilePage";
import { useClientNotes } from "../../hooks/useClientNotes";
import { ContactHistoryList } from "./components/ContactHistoryList";
import { CoordinatorNoteItem } from "./components/CoordinatorNoteItem";
import type { RecordTab } from "./helpers/client";
import { isRecordTab, recordPath, alumnusPath, fullName, RECORD_TABS, sortContactsNewestFirst } from "./helpers/client";
import { useOpenedFrom } from "./helpers/openedFrom";

/**
 * An alum's full record: every coordinator note, and the whole contact history, one tab each. A child route of the profile, so it shares the alum the profile loaded and
 * the notes query the profile already ran — it opens from cache.
 *
 * Each tab is its own URL (`/alumni/:id/record/notes`), so a coordinator can link a colleague to it,
 * refresh without losing their place, and use Back to step out to the profile.
 */
export function AlumniRecordPage() {
    const { t } = useTranslation();
    const alumnus = useClient();
    const { tab } = useParams();
    // Carried on every link out, so the profile's back link still knows which page opened it.
    const opened = useOpenedFrom();
    const { notes, loading, loadFailed, editNote, deleteNote } = useClientNotes(alumnus.id);
    // Already loaded with the profile, which reads every contact (up to the API's page of 200).
    const contacts = sortContactsNewestFirst(alumnus.contacts);

    if (!isRecordTab(tab)) return <Navigate to={recordPath(alumnus.id, "notes")} state={opened} replace />;

    const tabLabels: Record<RecordTab, string> = {
        contacts: t("admin.alumni.record.contactsTab"),
        notes: t("admin.alumni.record.notesTab", { count: notes.length }),
    };

    return (
        <div className="flex flex-col gap-6 px-4 py-6 md:px-10">
            <Link
                to={alumnusPath(alumnus.id)}
                state={opened}
                className="text-text-muted hover:text-text-heading inline-flex w-fit items-center gap-1 text-sm"
            >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                {fullName(alumnus)}
            </Link>

            <header className="flex items-center gap-4">
                <Avatar size="lg" className="shrink-0">
                    <AvatarGradientFallback>{initialsFor(alumnus.firstName, alumnus.lastName)}</AvatarGradientFallback>
                </Avatar>
                <div className="min-w-0">
                    <Heading level={3} as="h1">
                        {t("admin.alumni.record.title")}
                    </Heading>
                    <Text size="sm" muted>
                        {t("admin.alumni.record.summary", { count: notes.length })}
                    </Text>
                </div>
            </header>

            {/* Links, not buttons: each tab is a URL, so Back and refresh behave. */}
            <nav aria-label={t("admin.alumni.record.sections")} className="border-border flex gap-6 border-b">
                {RECORD_TABS.map((entry) => (
                    <NavLink
                        key={entry}
                        to={recordPath(alumnus.id, entry)}
                        state={opened}
                        className={({ isActive }) =>
                            cn(
                                "-mb-px border-b-2 px-1 pb-3 text-sm transition-colors",
                                isActive
                                    ? "border-primary text-text-heading font-semibold"
                                    : "text-text-muted hover:text-text-heading border-transparent",
                            )
                        }
                    >
                        {tabLabels[entry]}
                    </NavLink>
                ))}
            </nav>

            {tab === "notes" ? (
                <section className="flex flex-col gap-3">
                    {loading ? (
                        <Text size="sm" muted>
                            {t("admin.alumni.notes.loading")}
                        </Text>
                    ) : loadFailed ? (
                        <Text size="sm" muted>
                            {t("admin.alumni.notes.loadFailed")}
                        </Text>
                    ) : notes.length === 0 ? (
                        <Text size="sm" muted>
                            {t("admin.alumni.notes.empty", { name: alumnus.firstName })}
                        </Text>
                    ) : (
                        <>
                            <Text size="sm" muted>
                                {t("admin.alumni.record.notesVisibility", { count: notes.length })}
                            </Text>
                            <Card width="full" className="px-5 py-2">
                                <ul className="divide-border divide-y">
                                    {notes.map((note) => (
                                        <CoordinatorNoteItem
                                            key={note.id}
                                            note={note}
                                            onEdit={(text) => editNote(note.id, text)}
                                            onDelete={() => deleteNote(note.id)}
                                        />
                                    ))}
                                </ul>
                            </Card>
                        </>
                    )}
                </section>
            ) : (
                <section>
                    {contacts.length === 0 ? (
                        <Text size="sm" muted>
                            {t("admin.alumni.history.empty")}
                        </Text>
                    ) : (
                        <Card width="full" className="px-5 py-5">
                            <ContactHistoryList contacts={contacts} />
                        </Card>
                    )}
                </section>
            )}
        </div>
    );
}
